import { Link, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import CustomTable from '../../../common/table/CustomTable';
import StatusChip from '../../../common/StatusChip';
import { ApplicantData, Column } from '../../../common/table/types';
import { applicantService } from '../../../../services/applicant.service';
import { navigation } from '../../../../utils/browser';
import { formatDate } from '../../../../utils/date';
import { STATUS_CONFIG } from '../../../../utils/common';
import { usePermissions } from '../../../../context/PermissionContext';
import { useNavigate } from 'react-router-dom';
import Notification from '../../../common/Notification';
import { useNotification } from '../../../../hooks/useNotification';
// removed SelectField in favor of searchable AsyncAutocomplete
import { AsyncAutocomplete, Option as AsyncOption } from '../../../common/AsyncAutocomplete';
import AddIcon from '@mui/icons-material/Add';
import { JIRA_TICKET_URL } from '../../../../utils/constants';
import { APP_ROUTES } from '../../../../constants/routes';
import { PAGE_IDS } from '../../../../constants/permissions';
import {
  getApplicantComparisonButtonState,
  hasApplicantProfileNavigate,
  isApplicantNameClickable,
  showCountryAddControl,
  showVacancyCodeAddControl,
} from '../../../../utils/applicantLogic';
import LinkedInConnectionStatusIcon from '../../../common/icons/LinkedInConnectionStatusIcon';
import ProcessStatusCell from './ProcessStatusCell';
import ProcessActionCell from './ProcessActionCell';
import ProcessHistoryDialog from './ProcessHistoryDialog';
import ConfirmationModal from '../../../common/ConfirmationModal';
import type { ActionType } from '../../../../services/types/processStatus.types';

const PROCESS_NA_TOOLTIP =
  'Manual actions are only available for WhatsApp and Email candidates';

function getProcessActionCellProps(row: ApplicantData): {
  enabled: boolean;
  actionType: ActionType | null | undefined;
  actionTooltip: string | undefined;
} {
  if (row.processStatusText === 'N/A') {
    return {
      enabled: false,
      actionType: null,
      actionTooltip: row.processActionTooltip?.trim() || PROCESS_NA_TOOLTIP,
    };
  }
  return {
    enabled: Boolean(row.processActionEnabled),
    actionType: row.processActionType,
    actionTooltip: row.processActionTooltip,
  };
}

function isProcessManualActionBlocked(row: ApplicantData | undefined): boolean {
  if (!row) return true;
  return row.processStatusText === 'N/A' || !row.processActionEnabled;
}

interface FetchApplicantsParams {
  status?: string | null;
  source?: string | null;
  vacancyCode?: string | null;
  vacancyOrName?: string | null;
  startDate?: string;
  endDate?: string;
  page: number;
  limit: number;
  sort?: string;
}

interface ApplicantTableProps {
  status?: string | null;
  source?: string | null;
  vacancyCode?: string | null;
  vacancyOrName?: string | null;
  startDate?: string;
  endDate?: string;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  userEmail?: string;
}

const ApplicantTable: React.FC<ApplicantTableProps> = ({
  status,
  source,
  vacancyCode,
  vacancyOrName,
  startDate,
  endDate,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  userEmail
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { pageId, setPageId, permissions } = usePermissions();
  const permissionIds = useMemo(
    () => permissions.map((p) => p.permissionId),
    [permissions],
  );
  const [applicants, setApplicants] = useState<ApplicantData[]>([]);
  const [isGeneratingComparison, setIsGeneratingComparison] = useState<number | null>(null);
  const [comparisonInitiated, setComparisonInitiated] = useState<Set<number>>(new Set());
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const { notification, hideNotification, showNotification } = useNotification();
  const [sortField, setSortField] = useState<keyof ApplicantData | undefined>("createdAt");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isVacancyDialogOpen, setIsVacancyDialogOpen] = useState(false);
  const [vacancies, setVacancies] = useState<{ vacancyCode: string; label: string }[]>([]);
  const [selectedApplicantForVacancy, setSelectedApplicantForVacancy] = useState<ApplicantData | null>(null);
  const [selectedVacancyId, setSelectedVacancyId] = useState<string>('');
  const [selectedVacancyOption, setSelectedVacancyOption] = useState<AsyncOption | null>(null);
  const [isSavingVacancy, setIsSavingVacancy] = useState(false);
  const [isCountryDialogOpen, setIsCountryDialogOpen] = useState(false);
  const [countries, setCountries] = useState<{ id: number; name: string; currency: string }[]>([]);
  const [selectedApplicantForCountry, setSelectedApplicantForCountry] = useState<ApplicantData | null>(null);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [selectedCountryOption, setSelectedCountryOption] = useState<AsyncOption | null>(null);
  const [isSavingCountry, setIsSavingCountry] = useState(false);

  // Process Status state (simplified - data comes from /search)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedApplicantForHistory, setSelectedApplicantForHistory] = useState<number | null>(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    actionType: ActionType;
    /** Stage to trigger when actionType is trigger_stage. */
    vacancyStageId?: number;
    applicantId: number;
    applicantName: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);



  const fetchApplicants = useCallback(async (params: FetchApplicantsParams) => {
    try {
      setLoading(true);
      const response = await applicantService.search({
        ...params,
        sort: sortField ? `${sortField}:${sortDirection}` : undefined,
      });
      setApplicants(response.data.data ? response.data.data : []);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error fetching applicants:', error);
    } finally {
      setLoading(false);
    }
  }, [sortField, sortDirection]);

  useEffect(() => {
    if (pageId !== PAGE_IDS.APPLICANT_SEARCH) {
      setPageId(PAGE_IDS.APPLICANT_SEARCH);
      return;
    }

    fetchApplicants({
      status,
      source,
      vacancyCode,
      vacancyOrName,
      startDate,
      endDate,
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage
    });
  }, [page, rowsPerPage, status, source, vacancyCode, vacancyOrName, startDate, endDate, pageId, setPageId, fetchApplicants]);

  const handleSort = (field: keyof ApplicantData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDetailsClick = async (applicantId: number) => {
    try {
      setIsLoadingDetails(true);
      setIsDialogOpen(true);

      // Call API to get applicant details
      const response = await applicantService.getFeedback(applicantId);

      const responseData = response.data as { feedback?: string };

      if (responseData && responseData.feedback) {
        setMarkdownContent(responseData.feedback);
      } else {
        // Set empty content if API doesn't return data
        setMarkdownContent('');
      }
    } catch (error) {
      console.error('Error fetching applicant details:', error);
      showNotification('Error loading applicant details. Please try again.', 'error');
      // Set empty content on error
      setMarkdownContent('');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Process Status handlers
  const handleStatusClick = (applicantId: number) => {
    setSelectedApplicantForHistory(applicantId);
    setHistoryDialogOpen(true);
  };

  const handleActionClick = (
    applicantId: number,
    applicantName: string,
    actionType: ActionType,
  ) => {
    const row = applicants.find((a) => a.id === applicantId);
    // The grid button follows the single action suggested by the backend.
    if (isProcessManualActionBlocked(row)) {
      return;
    }

    if (actionType === 'send_welcome') {
      setConfirmDialogConfig({
        title: 'Send Welcome Message',
        message: `Are you sure you want to send the welcome message to <strong>${applicantName}</strong>?`,
        actionType,
        applicantId,
        applicantName,
      });
      setConfirmDialogOpen(true);
      return;
    }

    const vacancyStageId = row?.processActionStageId ?? undefined;
    if (!vacancyStageId) {
      return;
    }
    // The button label is a common word; the confirmation says exactly which stage will be triggered.
    const stage = row?.processStageActions?.find((s) => s.vacancyStageId === vacancyStageId);
    const stageName = stage?.name || 'the next stage';
    const jiraStatus = stage?.jiraStatusName ? ` (Jira status: ${stage.jiraStatusName})` : '';
    const verb = stage?.actionText?.trim() || 'Allocate & Trigger';
    const allocateOnly = verb.toLowerCase() === 'allocate';
    const action = allocateOnly ? 'allocate' : 'allocate and trigger';

    setConfirmDialogConfig({
      title: verb,
      message:
        `You are about to ${action} the stage <strong>${stageName}</strong>${jiraStatus} ` +
        `for <strong>${applicantName}</strong>.` +
        `<br/><br/>${allocateOnly
          ? 'Its questions will be allocated so the answers can be recorded.'
          : 'Its questions will be allocated and the first question will be sent on WhatsApp.'}` +
        '<br/><br/>Do you want to continue?',
      actionType,
      vacancyStageId,
      applicantId,
      applicantName,
    });
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmDialogConfig || !userEmail) return;

    const { actionType, applicantId, applicantName, vacancyStageId } = confirmDialogConfig;
    const row = applicants.find((a) => a.id === applicantId);
    if (isProcessManualActionBlocked(row)) {
      setConfirmDialogOpen(false);
      setConfirmDialogConfig(null);
      return;
    }
    setActionLoading(applicantId);

    try {
      if (actionType === 'send_welcome') {
        const response = await applicantService.manualWelcome(applicantId, userEmail);
        if (response.data?.success === false) {
          showNotification(response.data.message || 'Welcome message could not be sent.', 'error');
          return;
        }
        showNotification(`Welcome message sent to ${applicantName}`, 'success');
      } else if (actionType === 'trigger_stage' && vacancyStageId) {
        // The backend answers 400 with the same body when the stage could not start (resolved, not thrown).
        const response = await applicantService.triggerStage(applicantId, vacancyStageId, userEmail);
        const result = response.data;
        if (!result?.started) {
          showNotification(result?.message || 'Stage could not be started.', 'error');
          return;
        }
        const stageName = result.stageName || 'Stage';
        // STAGE_COMPLETED: every answer already existed, so nothing was sent (the message says so).
        showNotification(
          result.action === 'STAGE_COMPLETED' && result.message
            ? result.message
            : `${stageName} questions started for ${applicantName}`,
          'success',
        );
      }

      await fetchApplicants({
        status,
        source,
        vacancyCode,
        vacancyOrName,
        startDate,
        endDate,
        page: page + 1,
        limit: rowsPerPage
      });

    } catch (error: any) {
      console.error('Error executing action:', error);
      const errorMsg = error.response?.data?.message || 'Action failed. Please try again.';
      showNotification(errorMsg, 'error');
    } finally {
      setActionLoading(null);
      setConfirmDialogOpen(false);
      setConfirmDialogConfig(null);
    }
  };

  const columns: Column<ApplicantData>[] = [
    {
      field: 'firstName',
      headerName: 'Name',
      sortable: true,
      width: '300px',
      type: 'text',
      renderCell: (row) => (
        <div
          style={{
            cursor: isApplicantNameClickable(permissionIds) ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (hasApplicantProfileNavigate(permissionIds)) {
              navigate(APP_ROUTES.APPLICANT.PROFILE(row.id));
            }
          }}
        >
          {`${row.firstName || ''} ${row.lastName || ''}`}
        </div>
      )
    },
    {
      field: 'vacancy',
      headerName: 'Vacancy',
      sortable: true,
      width: '320px',
      type: 'text'
    },
    {
      field: 'vacancyCode',
      headerName: 'VC Code',
      sortable: true,
      width: '80px',
      type: 'text',
      renderCell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>{row.vacancyCode || ''}</span>
          {showVacancyCodeAddControl(permissionIds, row.vacancyCode) && (
            <IconButton
              size="small"
              onClick={async () => {
                setSelectedApplicantForVacancy(row);
                setSelectedVacancyId('');
                setIsVacancyDialogOpen(true);
                if (vacancies.length === 0) {
                  try {
                    const response = await applicantService.getVacancyCodes();
                    const items = response.data?.data || [];
                    setVacancies(
                      items.map((x) => ({ vacancyCode: x.vacancy_Code, label: x.value })),
                    );
                  } catch (e) {
                    console.error('Error loading vacancies', e);
                    showNotification('Failed to load vacancies', 'error');
                  }
                }
              }}
              aria-label="Edit vacancy code"
            >
              <AddIcon fontSize="small" sx={{ color: '#702DFF' }} />
            </IconButton>
          )}
        </div>
      )
    },
    {
      field: 'source',
      headerName: 'Source',
      sortable: true,
      width: '120px',
      type: 'text'
    },
    {
      field: 'countryKey',
      headerName: 'Country',
      sortable: true,
      width: '80px',
      type: 'text',
      align: 'center',
      renderCell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
          <span>{row.countryKey || ''}</span>
          {showCountryAddControl(permissionIds, row.countryKey) && (
            <IconButton
              size="small"
              onClick={async () => {
                setSelectedApplicantForCountry(row);
                setSelectedCountryId(null);
                setIsCountryDialogOpen(true);
                if (countries.length === 0) {
                  try {
                    const response = await applicantService.getCountries();
                    const items = response.data?.data || [];
                    setCountries(items);
                  } catch (e) {
                    console.error('Error loading countries', e);
                    showNotification('Failed to load countries', 'error');
                  }
                }
              }}
              aria-label="Add country"
            >
              <AddIcon fontSize="small" sx={{ color: '#702DFF' }} />
            </IconButton>
          )}
        </div>
      )
    },
    {
      field: 'issueId',
      headerName: 'JIRA ID',
      sortable: true,
      width: '100px',
      type: 'text',
      renderCell: (row) => row.issueId ?
        <Link
          href={`${JIRA_TICKET_URL}${row.issueId}`}
          target="_blank" rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {row.issueId}
        </Link> : '-'
    },
    {
      field: 'linkedInConnectionStatus',
      headerName: '',
      sortable: false,
      width: '40px',
      type: 'text',
      align: 'center',
      renderCell: (row) => (
        <LinkedInConnectionStatusIcon
          status={row.linkedInConnectionStatus}
          variant="grid"
        />
      ),
    },
    {
      field: 'currentStatus',
      headerName: 'Status',
      sortable: true,
      width: '80px',
      type: 'status',
      renderCell: (row) => row.currentStatus ? <StatusChip status={row.currentStatus} statusConfig={STATUS_CONFIG} /> : '',
    },
    {
      field: 'processStatus',
      headerName: 'Process Status',
      sortable: false,
      width: '140px',
      type: 'status',
      renderCell: (row) => (
        <ProcessStatusCell
          statusText={row.processStatusText}
          statusColor={row.processStatusColor}
          stageName={row.processStageName}
          onStatusClick={() => handleStatusClick(row.id)}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      sortable: true,
      width: '50x',
      type: 'date',
      align: 'center',
      renderCell: (row) => formatDate(row.createdAt),
    },
    {
      field: 'elapsedTime',
      headerName: 'Elapsed Time',
      sortable: true,
      width: '65px',
      type: 'text',
      align: 'right',
      renderCell: (row) => `${row.elapsedTime} ${row.elapsedTime > 1 ? 'days' : 'day'}`,
    },
    {
      field: 'actions',
      headerName: '',
      width: '340px',
      sortable: false,
      type: 'text',
      renderCell: (row) => {
        const comparisonBtn = getApplicantComparisonButtonState(
          row.id,
          row,
          isGeneratingComparison,
          comparisonInitiated,
        );
        const processAction = getProcessActionCellProps(row);
        return (
          <div style={{ display: 'flex', gap: '8px', paddingLeft: '10px', alignItems: 'center' }}>
            {hasApplicantProfileNavigate(permissionIds) && (
              !row.vacancyCode ? (
                <Button
                  width="100px"
                  variant="contained"
                  style={{ height: '27px', fontSize: '12px' }}
                  disabled={!row.cvLink}
                  onClick={(e) => {
                    e.preventDefault();
                    if (row.cvLink) {
                      const url = row.cvLink.startsWith('http') ? row.cvLink : `https://${row.cvLink}`;
                      navigation.openInNewTab(url);
                    }
                  }}
                >
                  View CV
                </Button>
              ) : (
                <Button
                  width="100px"
                  variant="contained"
                  disabled={comparisonBtn.disabled}
                  isLoading={comparisonBtn.loadingLabel}
                  style={{ height: '27px', fontSize: '12px' }}
                  onClick={() => {
                    setIsGeneratingComparison(row.id);
                    applicantService.generateCVComparisonFeedback(row.id).then((response) => {
                      if (response.status === 200) {
                        showNotification('Comparison will be generated in background', 'success');
                        // Add to initiated set to keep button disabled
                        setComparisonInitiated(prev => new Set(prev).add(row.id));
                      } else {
                        showNotification('Error generating comparison. Please try again.', 'error');
                      }
                      setIsGeneratingComparison(null);
                    }).catch(() => {
                      showNotification('Error generating comparison. Please try again.', 'error');
                      setIsGeneratingComparison(null);
                    });
                  }}
                >
                  {isGeneratingComparison === row.id ? ' Generating...' : 'Comparison'}
                </Button>
              )
            )}
            {hasApplicantProfileNavigate(permissionIds) && (
              <Button
                width="80px"
                variant="contained"
                disabled={(row.overallScore ?? 0) <= 0}
                style={{
                  height: '27px',
                  fontSize: '12px',
                  backgroundColor: (row.overallScore ?? 0) <= 0 ? '#E0E0E0' :
                    (row.overallScore ?? 0) < 50 ? '#E9004A' :
                      (row.overallScore ?? 0) >= 80 ? '#528900' : '#E7B317',
                  color: (row.overallScore ?? 0) <= 0 ? '#9E9E9E' : 'white'
                }}
                onClick={() => handleDetailsClick(row.id)}
              >
                Feedback
              </Button>
            )}
            <ProcessActionCell
              actionType={processAction.actionType}
              actionText={row.processActionText}
              actionTooltip={processAction.actionTooltip}
              enabled={processAction.enabled}
              onActionClick={(actionType) =>
                handleActionClick(row.id, `${row.firstName} ${row.lastName}`, actionType)
              }
              isLoading={actionLoading === row.id}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {loading ? (
        <Loading message="Loading applicants..." />
      ) : (
        <CustomTable<ApplicantData>
          columns={columns}
          rows={applicants}
          onSort={handleSort}
          sortField={sortField}
          sortDirection={sortDirection}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}

      {/* Notification Component */}
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />

      {/* Feedback Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Applicant AI Feedback</span>
            <IconButton
              onClick={() => setIsDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            {isLoadingDetails ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Loading message="Loading applicant details..." />
              </div>
            ) : (
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', mb: 2, mt: 0 }}>
                      {children}
                    </Typography>
                  ),
                  h2: ({ children }) => (
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#444', mb: 1.5, mt: 3 }}>
                      {children}
                    </Typography>
                  ),
                  h3: ({ children }) => (
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#555', mb: 1, mt: 2 }}>
                      {children}
                    </Typography>
                  ),
                  p: ({ children }) => (
                    <Typography sx={{ mb: 1, color: '#333' }}>
                      {children}
                    </Typography>
                  ),
                  ul: ({ children }) => (
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li style={{ margin: '4px 0', color: '#333' }}>
                      {children}
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ fontWeight: 700, color: '#702DFF' }}>
                      {children}
                    </strong>
                  ),
                }}
              >
                {markdownContent}
              </ReactMarkdown>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setIsDialogOpen(false)}
            width="100px"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Vacancy Code Dialog */}
      <Dialog
        open={isVacancyDialogOpen}
        onClose={() => setIsVacancyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            padding: '1rem 1rem 1rem 1rem',
            borderRadius: '15px'
          }
        }}
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Add Vacancy Code</div>
              {selectedApplicantForVacancy && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {`${selectedApplicantForVacancy.firstName} ${selectedApplicantForVacancy.lastName}`} Â· {selectedApplicantForVacancy.issueId || 'No Issue'}
                </div>
              )}
            </div>
            <IconButton
              onClick={() => setIsVacancyDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            <AsyncAutocomplete
              label="Vacancy"
              placeholder="Search Vacancy"
              value={selectedVacancyOption}
              onChange={(option) => {
                setSelectedVacancyOption(option);
                setSelectedVacancyId(option?.value || '');
              }}
              loadOptions={async (query: string) => {
                const q = query.toLowerCase();
                const source = vacancies || [];
                const filtered = source.filter(v =>
                  !q || v.vacancyCode.toLowerCase().includes(q) || v.label.toLowerCase().includes(q)
                );
                return filtered.map(v => ({ value: v.vacancyCode, label: v.label }));
              }}
              width="100%"
              debounceMs={0}
              loadOptionsOnOpen={true}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setIsVacancyDialogOpen(false);
              setSelectedApplicantForVacancy(null);
              setSelectedVacancyId('');
              setSelectedVacancyOption(null);
            }}
            width="100px"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            width="120px"
            isLoading={isSavingVacancy}
            disabled={!selectedVacancyId}
            onClick={async () => {
              if (!selectedApplicantForVacancy || !selectedVacancyId) return;

              try {
                setIsSavingVacancy(true);

                const payload = {
                  applicantId: selectedApplicantForVacancy.id,
                  issueId: selectedApplicantForVacancy.issueId,
                  vacancyCode: selectedVacancyId,
                };

                await applicantService.updateVacancyCode(payload);

                showNotification('Vacancy code updated', 'success');
                setIsVacancyDialogOpen(false);
                setSelectedApplicantForVacancy(null);
                setSelectedVacancyId('');
                setSelectedVacancyOption(null);
                fetchApplicants({
                  status,
                  source,
                  vacancyCode,
                  vacancyOrName,
                  startDate,
                  endDate,
                  page: page + 1,
                  limit: rowsPerPage
                });
              } catch (e) {
                console.error('Error updating vacancy code', e);
                showNotification('Failed to update vacancy code', 'error');
              } finally {
                setIsSavingVacancy(false);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Country Dialog */}
      <Dialog
        open={isCountryDialogOpen}
        onClose={() => setIsCountryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            padding: '1rem 1rem 1rem 1rem',
            borderRadius: '15px'
          }
        }}
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>Add Country</div>
              {selectedApplicantForCountry && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {`${selectedApplicantForCountry.firstName} ${selectedApplicantForCountry.lastName}`} Â· {selectedApplicantForCountry.issueId || 'No Issue'}
                </div>
              )}
            </div>
            <IconButton
              onClick={() => setIsCountryDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
            <AsyncAutocomplete
              label="Country"
              placeholder="Search Country"
              value={selectedCountryOption}
              onChange={(option) => {
                setSelectedCountryOption(option);
                setSelectedCountryId(option?.value ? Number(option.value) : null);
              }}
              loadOptions={async (query: string) => {
                const q = query.toLowerCase();
                const source = countries || [];
                const filtered = source.filter(c =>
                  !q || c.name.toLowerCase().includes(q)
                );
                return filtered.map(c => ({ value: String(c.id), label: c.name }));
              }}
              width="100%"
              debounceMs={0}
              loadOptionsOnOpen={true}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setIsCountryDialogOpen(false);
              setSelectedApplicantForCountry(null);
              setSelectedCountryId(null);
              setSelectedCountryOption(null);
            }}
            width="100px"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            width="120px"
            isLoading={isSavingCountry}
            disabled={!selectedCountryId}
            onClick={async () => {
              if (!selectedApplicantForCountry || !selectedCountryId) return;

              try {
                setIsSavingCountry(true);

                const payload = {
                  countryId: selectedCountryId,
                };

                await applicantService.updateCountry(selectedApplicantForCountry.id, payload);

                showNotification('Country updated successfully', 'success');
                setIsCountryDialogOpen(false);
                setSelectedApplicantForCountry(null);
                setSelectedCountryId(null);
                setSelectedCountryOption(null);
                fetchApplicants({
                  status,
                  source,
                  vacancyCode,
                  vacancyOrName,
                  startDate,
                  endDate,
                  page: page + 1,
                  limit: rowsPerPage
                });
              } catch (e) {
                console.error('Error updating country', e);
                showNotification('Failed to update country', 'error');
              } finally {
                setIsSavingCountry(false);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Process History Dialog */}
      <ProcessHistoryDialog
        open={historyDialogOpen}
        applicantId={selectedApplicantForHistory || 0}
        onClose={() => {
          setHistoryDialogOpen(false);
          setSelectedApplicantForHistory(null);
        }}
        onViewDetails={
          hasApplicantProfileNavigate(permissionIds) && selectedApplicantForHistory
            ? () => {
                const applicantId = selectedApplicantForHistory;
                setHistoryDialogOpen(false);
                setSelectedApplicantForHistory(null);
                navigate(APP_ROUTES.APPLICANT.ADDITIONAL_INFO(applicantId));
              }
            : undefined
        }
      />

      {/* Confirmation Dialog for Process Actions */}
      <ConfirmationModal
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setConfirmDialogConfig(null);
        }}
        onConfirm={handleConfirmAction}
        title={confirmDialogConfig?.title || ''}
        message={confirmDialogConfig?.message || ''}
        confirmText="Confirm"
        cancelText="Cancel"
        isActioning={actionLoading !== null}
      />
    </div>
  );
};

export default ApplicantTable; 
